import {
  BadgeCheck,
  Building2,
  ChartPie,
  ClipboardList,
  Fish,
  GraduationCap,
  ListChecks,
  Newspaper,
  ScrollText,
  Settings,
  ShieldCheck,
  Siren,
  Trophy,
  Users,
} from 'lucide-react'
import { isEnglish } from '../lib/i18n'

/**
 * The admin side menu, cloned from Views/Shared/_AdminsBaseLayout.cshtml (lines 438-1017).
 *
 * Every group and link keeps the legacy order and the Razor guard that decides
 * whether it renders. `legacy` records the MVC controller each route maps to so
 * the API wiring has an anchor later.
 *
 * LABELS: the legacy Arabic is kept except where it is marked `Renamed:` — a
 * short set you approved, covering two missing hamzas and four labels that said
 * their own group's name back to you (`إحصائيات عامة` under `الإحصائيات`,
 * `إدارة البرامج التدريبية` under `البرامج التدريبية`, `قوالب/حملات الإصطياد`
 * under `صياد`). Page titles come from each feature's own strings.js and are
 * untouched, so nothing here changes what a page calls itself.
 */

/** CoEIA.Rawam.Commons.Constants.ROLES */
export const ROLES = {
  SystemManagers: 'SystemManagers',
  /* Held by real accounts and gating the dashboard's own two report buttons in the legacy
     (`Views/General/Index.cshtml:79`), but absent from this table until those buttons were
     built — which also meant `isAdminSession` did not count a statistics-only admin as one. */
  StatisticsManagers: 'StatisticsManagers',
  SettingsManagers: 'SettingsManagers',
  AdvancedSettingsManagers: 'AdvancedSettingsManagers',
  UsersManagers: 'UsersManagers',
  ContentManagers: 'ContentManagers',
  PoliciesManagers: 'PoliciesManagers',
  SecurityReportsManagers: 'SecurityReportsManagers',
  AssessmentManagers: 'AssessmentManagers',
  AwarenessContentManagers: 'AwarenessContentManagers',
  GamificationManagers: 'GamificationManagers',
  SurveyManagers: 'SurveyManagers',
  CertificateValiditionManagers: 'CertificateValiditionManagers',
  SystemLogsManagers: 'SystemLogsManagers',
}

/** Canonical tenant administration roles. The platform-only SystemManagers role is excluded. */
export const TENANT_MANAGER_ROLES = [
  ROLES.UsersManagers,
  ROLES.SettingsManagers,
  ROLES.SystemLogsManagers,
  ROLES.PoliciesManagers,
  ROLES.ContentManagers,
  ROLES.AdvancedSettingsManagers,
  ROLES.SecurityReportsManagers,
  ROLES.AssessmentManagers,
  ROLES.AwarenessContentManagers,
  ROLES.GamificationManagers,
  ROLES.SurveyManagers,
  ROLES.CertificateValiditionManagers,
  ROLES.StatisticsManagers,
]

/** LicenseFeatureSet — `Gamififcation` keeps the legacy spelling on purpose. */
export const FEATURES = {
  Lms: 'Lms',
  Policies: 'Policies',
  Phishing: 'Phishing',
  SecurityReports: 'SecurityReports',
  Assessments: 'Assessments',
  AwarenessContent: 'AwarenessContent',
  Gamififcation: 'Gamififcation',
  Surveys: 'Surveys',
}

const adminMenuAr = [
  {
    id: 'statisticsGroup',
    // Renamed: the legacy drops the hamza on the alif.
    title: 'الإحصائيات',
    icon: ChartPie,
    roles: TENANT_MANAGER_ROLES,
    excludeRoles: [ROLES.SystemManagers],
    items: [
      // Renamed: `إحصائيات عامة` under a group already called `الإحصائيات` said
      // the same word twice.
      { id: 'general', title: 'نظرة عامة', to: '/general', legacy: 'General' },
      {
        id: 'adminLicense',
        title: 'الرخصة',
        to: '/license',
        legacy: 'AdminLicenseManagement',
        roles: [ROLES.SettingsManagers],
      },
    ],
  },
  {
    id: 'usersGroup',
    title: 'المستخدمين',
    icon: Users,
    roles: [ROLES.UsersManagers],
    items: [
      { id: 'users', title: 'قائمة المستخدمين', to: '/users', legacy: 'Users', roles: [ROLES.UsersManagers] },
      { id: 'groups', title: 'الإدارات', to: '/groups', legacy: 'Groups', roles: [ROLES.UsersManagers] },
      {
        id: 'invitations',
        title: 'الدعوات',
        to: '/invitations',
        legacy: 'Invitations',
        roles: [ROLES.UsersManagers],
        setting: 'ActivateInvitations',
      },
    ],
  },
  {
    id: 'contentGroup',
    title: 'البرامج التدريبية',
    icon: GraduationCap,
    roles: [ROLES.ContentManagers],
    feature: FEATURES.Lms,
    items: [
      {
        id: 'programs',
        // Renamed: it repeated its own group name in full.
        title: 'قائمة البرامج',
        to: '/programs',
        legacy: 'ProgramsManagement',
        roles: [ROLES.ContentManagers],
      },
      /* NOT IN THE LEGACY'S MENU EITHER. `Views/ManageExams/Index.cshtml` and its
         controller exist, but nothing links to them: the admin sidebar's thirty-three
         entries do not include ManageExams, and exams are reached from a course — the
         green question-mark on the courses table, `showExamForm(courseId)`. This screen
         was given a top-level menu item it never had, which put a list of every exam in
         the product where the legacy has a per-course action.

         The route and the page stay, so `/admin/manageexams` still lands somewhere (R1),
         and so does the legacy's own URL-only page. Only the menu entry goes. */
      {
        id: 'exams',
        title: 'إدارة الاختبارات',
        to: '/exams',
        legacy: 'ManageExams',
        roles: [ROLES.ContentManagers],
        hidden: true,
      },
      // style="display:none" in the legacy layout — kept for the record, never rendered.
      {
        id: 'programsReports',
        title: 'تقرير البرامج التدريبية',
        to: '/programs-reports',
        legacy: 'LearningProgramsReports',
        roles: [ROLES.ContentManagers],
        hidden: true,
      },
      {
        id: 'categories',
        title: 'إدارة المواضيع',
        to: '/categories',
        legacy: 'CategoriesManagement',
        roles: [ROLES.ContentManagers],
      },
      {
        id: 'questions',
        title: 'قائمة الأسئلة',
        to: '/questions',
        legacy: 'EditQuestions',
        roles: [ROLES.ContentManagers],
      },
      // style="display:none"
      {
        id: 'questionsStatistics',
        title: 'إحصائيات الأسئلة',
        to: '/questions-statistics',
        legacy: 'Qusetions',
        roles: [ROLES.ContentManagers],
        hidden: true,
      },
      {
        id: 'certificateSettings',
        title: 'إدارة الشهادات',
        to: '/certificates',
        legacy: 'CertificateSettings',
        roles: [ROLES.ContentManagers, ROLES.CertificateValiditionManagers],
      },
      // style="display:none"
      {
        id: 'printCertificate',
        title: 'طباعة الشهادة',
        to: '/print-certificate',
        legacy: 'PrintCertificate',
        roles: [ROLES.ContentManagers],
        hidden: true,
      },
    ],
  },
  {
    id: 'policyGroup',
    title: 'السياسات',
    icon: ShieldCheck,
    roles: [ROLES.PoliciesManagers],
    feature: FEATURES.Policies,
    items: [
      {
        id: 'policiesSettings',
        title: 'قائمة السياسات',
        to: '/policies',
        legacy: 'WorkspacePoliciesSettings',
      },
    ],
  },
  {
    id: 'phishingGroup',
    title: 'صياد',
    icon: Fish,
    /* `ContentManagers`, NOT `SettingsManagers` — every endpoint in the five phishing
       slices carries `RequireRole("ContentManagers")`. Gating the rail on the other role
       meant the only admins offered phishing were refused by the API on every screen, while
       the admins the API accepts were never shown the entry. The API wins (CLAUDE.md), so
       the menu is what moves. */
    roles: [ROLES.ContentManagers],
    feature: FEATURES.Phishing,
    items: [
      {
        id: 'phishingTemplates',
        // Renamed: `صياد` above them already says which قوالب / حملات these are.
        title: 'القوالب',
        to: '/phishing/templates',
        legacy: 'PhishingTemplates',
      },
      {
        id: 'phishingCampaigns',
        title: 'الحملات',
        to: '/phishing/campaigns',
        legacy: 'PhishingCampaigns',
      },
      {
        id: 'phishingCategories',
        title: 'الإعدادات',
        to: '/phishing/settings',
        legacy: 'PhishingCategories',
      },
    ],
  },
  {
    id: 'securityReportsGroup',
    title: 'الحوادث السيبرانية',
    icon: Siren,
    roles: [ROLES.SecurityReportsManagers],
    feature: FEATURES.SecurityReports,
    items: [
      {
        id: 'securityReports',
        title: 'قائمة البلاغات',
        to: '/security-reports',
        legacy: 'SecurityReportsManagement',
      },
    ],
  },
  {
    id: 'assessmentGroup',
    title: 'القياسات',
    icon: ClipboardList,
    roles: [ROLES.AssessmentManagers],
    feature: FEATURES.Assessments,
    items: [
      {
        id: 'assessments',
        title: 'إدارة القياسات',
        to: '/assessments-management',
        legacy: 'AssessmentsManagement',
      },
    ],
  },
  {
    id: 'awarenessContentGroup',
    title: 'النشرات التوعوية',
    icon: Newspaper,
    roles: [ROLES.AwarenessContentManagers],
    feature: FEATURES.AwarenessContent,
    items: [{ id: 'awareness', title: 'قائمة النشرات', to: '/awareness', legacy: 'Awareness' }],
  },
  {
    id: 'gamificationGroup',
    title: 'التلعيب',
    icon: Trophy,
    roles: [ROLES.GamificationManagers],
    feature: FEATURES.Gamififcation,
    items: [
      {
        id: 'pointsAnalysis',
        title: 'النتائج',
        to: '/gamification/analysis',
        legacy: 'PointsAnalysis',
      },
      {
        id: 'pointsTransactions',
        title: 'سجل العمليات',
        to: '/gamification/transactions',
        legacy: 'PointsTransactions',
      },
      {
        id: 'pointsResetLogs',
        title: 'تصفير النقاط',
        to: '/gamification/reset-logs',
        legacy: 'PointsResetLogs',
      },
      {
        id: 'pointsActivities',
        title: 'الإعدادات',
        to: '/gamification/settings',
        legacy: 'PointsActivities',
      },
    ],
  },
  {
    id: 'certificateValidationGroup',
    // Renamed: reads as a rail label rather than a sentence, and this group
    // renders as a single direct link.
    title: 'التحقق من الشهادات',
    icon: BadgeCheck,
    roles: [ROLES.CertificateValiditionManagers],
    items: [
      {
        id: 'certificateValidation',
        title: 'التحقق',
        to: '/certificate-validation',
        legacy: 'CertificateValidation',
      },
    ],
  },
  {
    id: 'surveyGroup',
    title: 'الاستبيانات',
    icon: ListChecks,
    roles: [ROLES.SurveyManagers],
    feature: FEATURES.Surveys,
    items: [
      {
        id: 'surveys',
        title: 'إدارة الاستبيانات',
        to: '/surveys-management',
        legacy: 'SurveysManagement',
      },
    ],
  },
  {
    id: 'settingsGroup',
    title: 'الإعدادات',
    icon: Settings,
    roles: [ROLES.SettingsManagers],
    items: [
      { id: 'settings', title: 'إعدادات عامة', to: '/settings', legacy: 'Settings' },
      {
        id: 'profileMapping',
        title: 'إعدادات متقدمة',
        to: '/settings/advanced',
        legacy: 'ProfileMapping',
        roles: [ROLES.AdvancedSettingsManagers],
      },
      {
        id: 'settingsKeys',
        title: 'بيانات الإعدادات',
        to: '/settings/keys',
        legacy: 'SettingsKeys',
        roles: [ROLES.AdvancedSettingsManagers],
      },
    ],
  },
  {
    id: 'systemLogsGroup',
    title: 'سجلات النظام',
    icon: ScrollText,
    /* The two screens in this group are guarded by DIFFERENT roles server-side, which only
       became visible once the activity log started calling the API instead of a mock: the group
       was gated on SystemLogsManagers alone, so a manager with that role was offered سجل النظام
       and got a 403 from `/SystemState/GetActivities`, which requires AdvancedSettingsManagers.
       The group now admits either role and each item carries the one its own endpoint enforces,
       so nobody is shown a screen they cannot load (R2 — the API wins). */
    roles: [ROLES.SystemLogsManagers, ROLES.AdvancedSettingsManagers],
    excludeRoles: [ROLES.SystemManagers],
    items: [
      {
        id: 'systemState',
        title: 'سجل النظام',
        to: '/system-state',
        legacy: 'SystemState',
        roles: [ROLES.AdvancedSettingsManagers],
      },
      {
        id: 'notificationsDetails',
        title: 'سجل التنبيهات',
        to: '/notifications-log',
        legacy: 'NotificationsDetails',
        roles: [ROLES.SystemLogsManagers],
      },
    ],
  },
  {
    id: 'companiesGroup',
    // Renamed: the legacy drops the hamza on the alif.
    title: 'إدارة المنظمات',
    icon: Building2,
    roles: [ROLES.SystemManagers],
    items: [
      {
        id: 'manageCompanies',
        title: 'قائمة المنظمات',
        to: '/companies',
        legacy: 'ManageCompanies',
      },
      {
        id: 'licenseManagement',
        title: 'رخصة الاستخدام',
        to: '/companies/license',
        legacy: 'LicenseManagement',
      },
    ],
  },
]

/**
 * The same rail in English, keyed by the ids above.
 *
 * Wording is the legacy Sitemap/Master English resources, with the four
 * `Renamed:` shortenings carried across: a link under `Statistics` is called
 * `Overview`, not `General Statistics`, for the same reason it is called
 * `نظرة عامة` in Arabic — the group has already said the noun.
 */
const titlesEn = {
  statisticsGroup: 'Statistics',
  general: 'Overview',
  adminLicense: 'License',

  usersGroup: 'Users',
  users: 'Users List',
  groups: 'Departments',
  invitations: 'Invitations',

  contentGroup: 'Programs',
  programs: 'Programs List',
  /* The only id this table was missing — the English build printed the Arabic
     label in the rail, and now in the browser tab as well. */
  exams: 'Manage Exams',
  programsReports: 'Programs Reports',
  categories: 'Manage Topics',
  questions: 'Questions List',
  questionsStatistics: 'Questions Statistics',
  certificateSettings: 'Certificate Settings',
  printCertificate: 'Print Certificate',

  policyGroup: 'Policies',
  policiesSettings: 'Policies List',

  phishingGroup: 'Phishing',
  phishingTemplates: 'Templates',
  phishingCampaigns: 'Campaigns',
  phishingCategories: 'Settings',

  securityReportsGroup: 'Security Reports',
  securityReports: 'Reports List',

  assessmentGroup: 'Assessments',
  assessments: 'Manage Assessments',

  awarenessContentGroup: 'Awareness Content',
  awareness: 'Content List',

  gamificationGroup: 'Gamification',
  pointsAnalysis: 'Results',
  pointsTransactions: 'Transactions',
  pointsResetLogs: 'Points Reset Logs',
  pointsActivities: 'Settings',

  certificateValidationGroup: 'Certificate Validation',
  certificateValidation: 'Validate',

  surveyGroup: 'Surveys',
  surveys: 'Manage Surveys',

  settingsGroup: 'Settings',
  settings: 'General Settings',
  profileMapping: 'Advanced Settings',
  settingsKeys: 'Settings Values',

  systemLogsGroup: 'System Logs',
  systemState: 'Activity Log',
  notificationsDetails: 'Notifications Log',

  companiesGroup: 'Organizations',
  manageCompanies: 'Companies List',
  licenseManagement: 'Usage License',
}

const inEnglish = (menu) =>
  menu.map((group) => ({
    ...group,
    title: titlesEn[group.id] ?? group.title,
    items: group.items.map((item) => ({ ...item, title: titlesEn[item.id] ?? item.title })),
  }))

export const adminMenu = isEnglish ? inEnglish(adminMenuAr) : adminMenuAr

/**
 * The legacy role / licence / setting guard for one entry.
 *
 * Exported because the rail is no longer the only thing that has to respect it:
 * the command search indexes destinations INSIDE pages, and a section of a page
 * you cannot open must not be offered — see uiIndex.js.
 */
export function permits(entry, session) {
  if (entry.hidden) return false
  if (entry.roles && !entry.roles.some((role) => session.roles.includes(role))) return false
  if (entry.excludeRoles?.some((role) => session.roles.includes(role))) return false
  // Legacy: `FeatureSets.Count == 0 || FeatureSets.Contains(x)` — an empty license grants everything.
  if (entry.feature && session.features.length > 0 && !session.features.includes(entry.feature)) return false
  if (entry.setting && session.settings[entry.setting] !== 'true') return false
  return true
}

/**
 * Whether this account belongs in the admin panel at all — what the legacy layout called
 * `ViewBag.isAdmin`, and what decides where `/` sends someone.
 *
 * NOT `visibleMenu(session).length > 0`, tempting as that is: the statistics group carries no
 * role guard, so the overview is visible to anyone signed in and the menu is never empty.
 * Every OTHER guard in this file names a role from `ROLES`, and `ROLES` holds the thirteen
 * manager roles and nothing else — `Users`, which every learner has, is not among them. So
 * holding any one of them is exactly the question being asked, and the answer stays correct
 * as the menu grows, because a new section guards itself with a role from the same table.
 */
export function isAdminSession(session) {
  const held = session?.roles ?? []
  return Object.values(ROLES).some((role) => held.includes(role))
}

/** Applies the legacy role / license / settings guards and drops emptied groups. */
export function visibleMenu(session) {
  return adminMenu
    .filter((group) => permits(group, session))
    .map((group) => ({ ...group, items: group.items.filter((item) => permits(item, session)) }))
    .filter((group) => group.items.length > 0)
}

/** The `{ group, item }` pair owning a menu id — how uiIndex resolves a trail. */
export function menuEntryById(id) {
  for (const group of adminMenu) {
    for (const item of group.items) {
      if (item.id === id) return { group, item }
    }
  }
  return null
}

/**
 * Flat route -> { group, item } index, used for the header context line.
 *
 * Matches the LONGEST entry that owns the route, so a detail page (`/users/7`)
 * resolves to its list (`/users`) while `/settings/advanced` still beats
 * `/settings`.
 */
export function findMenuEntry(pathname) {
  let best = null
  for (const group of adminMenu) {
    for (const item of group.items) {
      const owns = pathname === item.to || pathname.startsWith(`${item.to}/`)
      if (owns && (!best || item.to.length > best.item.to.length)) best = { group, item }
    }
  }
  return best
}
